import useQuery from '@/hooks/useQuery';
import { hash } from '@/utils/hash';
import { stringify } from '@/utils/querystring';
import get from 'lodash/get';
import Queue from 'queue-promise';
import { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Skeleton } from './ui/skeleton';
import { useConfig } from '@/config/config';
import { Predicate } from '@/gql/graphql';

const concurrent = 10;
const interval = 0;

const queues: {
  [key: string]: Queue;
} = {};

export type CountProps = {
  apiEndpoint: string;
  params?: Record<string, undefined | string | number | (number | string)[]>;
  queueId?: string;
  property?: string;
  responseIsNumber?: boolean;
};

type Props = CountProps & {
  message?: string;
};

export function Count({ apiEndpoint, params, queueId, property, message }: Props) {
  const { count, loading, error } = useCount({ apiEndpoint, params, queueId, property });

  if (loading || typeof count === 'undefined') {
    return (
      <Skeleton className="g-inline">
        <span className="g-opacity-0">Loading</span>
      </Skeleton>
    );
  }
  if (error) return false;

  if (message) {
    return <FormattedMessage id={message} values={{ total: count }} />;
  } else {
    return <FormattedNumber value={count} />;
  }
}

const LITERATURE_COUNT_QUERY = `
  query($predicate: Predicate) {
    search: literatureSearch(predicate: $predicate) {
      documents {
        total
      }
    }
  }
`;
const OCCURRENCE_COUNT_QUERY = `
  query($predicate: Predicate) {
    search: occurrenceSearch(predicate: $predicate) {
      documents {
        total
      }
    }
  }
`;
export function useLiteratureCount({ predicate }: { predicate: any }) {
  return useGraphQLCount({ query: LITERATURE_COUNT_QUERY, predicate });
}
export function useOccurrenceCount({ predicate }: { predicate: any }) {
  return useGraphQLCount({ query: OCCURRENCE_COUNT_QUERY, predicate });
}
export function useSiteOccurrenceCount({ predicate }: { predicate: any }) {
  const { occurrenceSearch } = useConfig();
  const scope = occurrenceSearch?.scope;
  const modifiedPredicate = scope
    ? {
        type: 'and',
        predicates: [scope, predicate],
      }
    : predicate;
  return useGraphQLCount({ query: OCCURRENCE_COUNT_QUERY, predicate: modifiedPredicate });
}

export function useGraphQLCount({ predicate, query }: { predicate: any; query: string }) {
  const { data, load, error, loading } = useQuery<
    { search?: { documents: { total: number } } },
    { predicate: any }
  >(query, {
    lazyLoad: true,
    variables: { predicate },
    queue: {
      name: 'graphql-counts',
    },
  });

  const predicateId = hash(JSON.stringify(predicate));

  useEffect(() => {
    if (predicate) {
      load({
        variables: {
          predicate,
        },
      });
    }
    // We are tracking the params via a calculated ID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predicateId, load]);

  return { count: data?.search?.documents.total, error, loading };
}

export function useCount({
  apiEndpoint,
  params = {},
  queueId = 'counts',
  property = 'count',
  responseIsNumber = false,
}: CountProps) {
  const [count, setCount] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const paramId = hash(
    JSON.stringify({
      apiEndpoint,
      params,
    })
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const endpoint = `${apiEndpoint}?${stringify({
      limit: '0',
      ...params,
    })}`;

    setLoading(true);

    // create a function to start the request. we will put this in the queue afterwards
    const startRequest = () =>
      fetch(endpoint, { signal })
        .then((response) => response.json())
        .then((data) => {
          setLoading(false);
          setError(false);
          const total = responseIsNumber ? data : get(data, property);
          if (typeof total === 'number') {
            setCount(total);
          }
        })
        .catch((err) => {
          setLoading(false);
          if (err.name === 'AbortError') {
            setError(false);
          } else {
            setError(true);
          }
        });

    // if no queue exists, create a new one
    if (!queues[queueId]) {
      queues[queueId] = new Queue({
        concurrent,
        interval,
        start: true,
      });
    }

    // add the request to the queue
    queues[queueId].enqueue(startRequest);

    return () => {
      // cancel the request before unmounting
      controller.abort();
    };
    // We are tracking the params via a calculated ID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEndpoint, property, paramId, queueId, responseIsNumber]);

  return { count, loading, error };
}

export function SiteOccurrenceCount({
  predicate,
  message,
}: {
  predicate: Predicate;
  message?: string;
}) {
  const { loading, count, error } = useSiteOccurrenceCount({ predicate });
  if (loading || typeof count === 'undefined') {
    return (
      <Skeleton className="g-inline g-text-sm">
        <span className="g-opacity-0">Loading</span>
      </Skeleton>
    );
  }
  if (error) return false;

  if (message) {
    return <FormattedMessage id={message} values={{ total: count }} />;
  } else {
    return <FormattedNumber value={count} />;
  }
}
